import { logger } from '@gik/analytics/utils/logger';
import { useInkind } from '@gik/api/inkinds/inkind';
import { uploadInkindCoverPhoto } from '@gik/api/inkinds/inkindCoverPhoto';
import { openContactPageOrganizersModal } from '@gik/calendar';
import { defaultPageImagePath } from '@gik/core/constants';
import { useIsRetina } from '@gik/core/hooks/CustomHooks';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import sleep from '@gik/core/utils/sleep';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { updatePagePhoto } from '@gik/inkind-page/api/InkindPageAPI';
import { FollowPageCTA } from '@gik/inkind-page/components/FollowPageCTA/FollowPageCTA';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { PageAvatar } from '@gik/ui/Avatar';
import { AvatarRemote } from '@gik/ui/Avatar/AvatarRemote';
import { BackgroundImage } from '@gik/ui/BackgroundImage';
import { Button } from '@gik/ui/Button';
import { Container } from '@gik/ui/Container';
import { openImagePickerModal } from '@gik/ui/gik/ImagePickerModal';
import { AnimatedDivBone, ParagraphBone } from '@gik/ui/SkeletonLoader';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { Text } from '@gik/ui/Text/Text';
import { Tooltip } from '@gik/ui/Tooltip';
import { UI } from '@gik/ui/UIManager';
import { LightboxMedia } from '@gik/ui/UIManager/UILightboxTypes';
import CameraIcon from '@heroicons/react/solid/CameraIcon';
import CogIcon from '@heroicons/react/solid/CogIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import usePrevious from 'react-use/lib/usePrevious';
import { openInkindPageSettingsModal } from '../InkindPageSettingsModal/InkindPageSettingsModal';
import { inkindPageTourSelectors } from '../InkindPageTour/InkindPageTour';
import type { ICoverPhotoEditor } from './CoverPhotoEditor';
import { attachCoverPhotoImage } from './CoverPhotoEditor';
import { InkindCoverToolbar } from './InkindCoverToolbar';
import InkindCoverUploadImageControls from './InkindCoverUploadImageControls';
import { InkindPagePrivacyBadges } from './InkindPagePrivacyBadges';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode';

let coverPhotoEditor: ICoverPhotoEditor;
function InkindPageHeaderComp() {
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  // prettier-ignore
  const {
    data: inkindPage,
    mutate: mutateInkindPage,
  } = useInkind(inkindRouteId);
  const bem = bemBlock('inkind-page-header');
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);
  const isSamplePage = useInkindStore(state => state.isSamplePage);

  const isMounted = useIsMounted();
  const canUserEditPage = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();

  const isRetina = useIsRetina();

  // TODO: move to hook
  const [isEditingCoverPhoto, setEditingCoverPhoto] = React.useState(false);
  const [isSavingCoverPhoto, setSavingCoverPhoto] = React.useState(false);
  const [isChangingCoverColour, setChangingCoverColour] = React.useState(false);
  const coverColourIndex = inkindPage ? inkindPage.coverColourIndex : 0;
  const previousCoverColourIndex = usePrevious(coverColourIndex);

  async function onEditCoverPhoto() {
    coverPhotoEditor = await attachCoverPhotoImage();
    if (coverPhotoEditor) {
      // file was attached and processed successfully, launch edit mode
      setEditingCoverPhoto(true);
    }
  }

  function closeCoverPhotoEditor() {
    if (coverPhotoEditor) {
      coverPhotoEditor.destroy();
      coverPhotoEditor = null;
    }
    setEditingCoverPhoto(false);
  }

  function onCancelUploadCoverPhoto() {
    closeCoverPhotoEditor();
  }

  async function onCoverColourChange() {
    setChangingCoverColour(true);
    await sleep(1000);
    setChangingCoverColour(false);
  }

  async function onUploadCoverPhoto() {
    if (coverPhotoEditor) {
      const base64CoverPhotoImage = coverPhotoEditor.renderOutputImage();
      try {
        setSavingCoverPhoto(true);
        coverPhotoEditor.destroy();
        await uploadInkindCoverPhoto(inkindRouteId, base64CoverPhotoImage);
        await mutateInkindPage();
        // wait for the new image to load
        await sleep(2000);
        closeCoverPhotoEditor();
      } catch (error) {
        logger.error(error);
        UI.error('Failed to upload cover image. Please try again');
      }
      setSavingCoverPhoto(false);
    }
  }

  const organizers = inkindPage ? [inkindPage.owner, ...(inkindPage.organizers || [])] : null;

  function openOrganizersModal() {
    openContactPageOrganizersModal(inkindRouteId);
  }

  function openPageImageLightbox() {
    UI.lightbox([
      {
        mediaType: LightboxMedia.IMAGE,
        url: inkindPage.imgSrc || defaultPageImagePath,
        caption: inkindPage.title,
      },
    ]);
  }

  async function handleChangeCroppedImage(croppedImage: string) {
    await updatePagePhoto(inkindPage.routeId, croppedImage);
    mutateInkindPage();
  }

  function openChangePageImageModal() {
    openImagePickerModal({
      title: t(translationKeys.PageHeaderChangePageImageModalTitle),
      text: t(translationKeys.PageHeaderChangePageImageModalImagePickerText),
      fallback: inkindPage?.imgSrc || defaultPageImagePath,
      onChange: handleChangeCroppedImage,
    });
  }

  const handlePrivacyChanged = React.useCallback(async () => {
    await mutateInkindPage();
  }, [mutateInkindPage]);

  const isLoading = !inkindPage;

  const settingsSection = (
    <section className={bem('settings')}>
      {!isEditingCoverPhoto && canUserEditPage && (
        <InkindCoverToolbar
          inkindRouteId={inkindRouteId}
          onClickAddCoverPhoto={onEditCoverPhoto}
          onCoverColourChange={onCoverColourChange}
        />
      )}
      {!isEditingCoverPhoto && canUserEditPage && (
        <Button
          id={inkindPageTourSelectors.pageSettings}
          variant="secondary"
          pill
          onClick={() => openInkindPageSettingsModal({ inkindRouteId })}
          prepend={<SvgIcon Icon={CogIcon} />}
          className={bem('page-settings-btn')}
        >
          {t(translationKeys.PageHeaderPageSettings).toString()}
        </Button>
      )}
      {!canUserEditPage && <FollowPageCTA inkindRouteId={inkindRouteId} isSamplePage={isSamplePage} />}
    </section>
  );

  const privacySection = (
    <section className={bem('privacy', [{ 'transparent-preview': isEditingCoverPhoto }])}>
      <InkindPagePrivacyBadges inkindRouteId={inkindRouteId} onChanged={handlePrivacyChanged} />
    </section>
  );

  const mainSection = (
    <section className={bem('main', [{ 'transparent-preview': isEditingCoverPhoto }])}>
      <div className={bem('bg-image-layer', [{ visible: inkindPage?.cover_src_retina || inkindPage?.cover_src }])} />
      <aside className={bem('avatar-wrapper')}>
        <div className={bem('change-inkind-photo')}>
          {canUserEditPage && (
            <Button variant={'secondary'} circle onClick={openChangePageImageModal}>
              <SvgIcon Icon={CameraIcon} />
            </Button>
          )}
        </div>

        {!isLoading ? (
          <PageAvatar
            className={bem('inkind-thumbnail')}
            imageUrl={inkindPage?.thumb_medium}
            circle={false}
            onClick={openPageImageLightbox}
          />
        ) : (
          <AnimatedDivBone className={bem('inkind-thumbnail')} />
        )}
      </aside>
      <main className={bem('header-info')}>
        <section className={bem('info-wrapper')}>
          {!isMdDown && settingsSection}
          <h1 className={bem('page-title')}>
            {!isLoading && inkindPage ? <Text>{inkindPage?.title}</Text> : <ParagraphBone words={5} />}
          </h1>
          <Debug
            data={
              'isWalletActive:' +
              inkindPage?.isWalletActive +
              ' | isWalletFeatureActive:' +
              inkindPage?.isWalletFeatureActive
            }
          />
          {isLoading ? (
            <h2>
              <ParagraphBone words={4} />
            </h2>
          ) : (
            inkindPage?.city &&
            inkindPage?.stateCode && (
              <h2 className={bem('page-location')}>
                {inkindPage?.city}, {inkindPage?.stateCode}
              </h2>
            )
          )}
        </section>
        <section className={bem('organizers-section')}>
          {!isLoading ? (
            <>
              <label className={bem('organizers-label')}>{t(translationKeys.organizersTitle).toString()}</label>
              {organizers
                .filter(item => item !== undefined)
                .map(organizer => {
                  return (
                    <Tooltip key={organizer.userId} text={organizer.name}>
                      <AvatarRemote
                        userId={organizer.userId}
                        className={bem('organizer-avatar')}
                        variant={'primary'}
                        onClick={openOrganizersModal}
                      />
                    </Tooltip>
                  );
                })}
            </>
          ) : (
            <ParagraphBone className={'tw-mb-2'} words={2} />
          )}
        </section>
        {!isMdDown && privacySection}
      </main>
    </section>
  );

  return (
    <div className={bem(null, [{ authenticated: isMounted() && !!userId }])}>
      <div className={bem('inner')} id="inkind-cover-content">
        {!isLoading && (
          <>
            <div className={bem('cover-gradient', undefined, `gradient${coverColourIndex}`)} />
            <div
              className={bem(
                'cover-gradient-prev',
                [{ changing: isChangingCoverColour }],
                `gradient${previousCoverColourIndex}`
              )}
            />
          </>
        )}
        {inkindPage?.cover_src_retina && (
          <BackgroundImage
            className={bem('cover-photo')}
            src={isRetina ? inkindPage?.cover_src_retina : inkindPage?.cover_src}
          />
        )}
        <div className={bem('cover-photo-editor', [{ open: isEditingCoverPhoto }])} id="inkind-cover-photo-editor">
          <img alt={'Cover photo'} id="inkind-cover-photo-editor-image" />
        </div>

        <Container className={bem('content')}>
          {isEditingCoverPhoto && (
            <InkindCoverUploadImageControls
              isSaving={isSavingCoverPhoto}
              onCancel={onCancelUploadCoverPhoto}
              onSave={onUploadCoverPhoto}
              direction={coverPhotoEditor?.direction}
            />
          )}
          {isMdDown ? (
            <>
              {settingsSection}
              {mainSection}
              {privacySection}
            </>
          ) : (
            <main className={bem('desktop-layout')}>{mainSection}</main>
          )}
        </Container>
      </div>
    </div>
  );
}

export const InkindPageHeader = withComponentErrorBoundary(InkindPageHeaderComp);
